<template>
    <Head :title="__('Create :resource', {resource: __('Feed').toLowerCase()})"/>

    <BreezeDashboardLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ __('Create :resource', {resource: __('Feed').toLowerCase()}) }}
            </h2>
        </template>

        <notification v-if="Object.keys(errors).length" color="danger">
            <ul class="list-inside">
                <li v-for="error in errors">{{ error }}</li>
            </ul>
        </notification>

        <the-modal-box v-model="isConfirmChangeSiteModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmChangeSite" @cancel="cancelChangeSite">
            <p>{{ __('Are you sure you want to change the site? It will remove selected nomenclature.') }}</p>
        </the-modal-box>

        <card-component :title="__('Feed Settings')" :icon="mdiListStatus" @submit.prevent="submit" form>
            <div class="md:grid md:grid-cols-2 md:gap-2">
                <div>
                    <field :label="__('Feed name')" :error="errors.name ?? ''">
                        <control :icon="mdiFormatColorText" v-model="form.name" :required="true"/>
                    </field>
                    <field :label="__('Site')" :error="errors.site ?? ''">
                        <control :icon="mdiLink" :placeholder="__('Site')" v-model="formSite" :options="siteOptions"
                                 :required="true"/>
                    </field>
                    <field :label="__('Feed type')" :error="errors.feed_type ?? ''">
                        <control :icon="mdiPuzzleOutline" :options="feedTypesOptions" v-model="form.feed_type"
                                 :required="true"/>
                    </field>
                    <field v-if="form.feed_type.id === 'xml'" :label="__('Update interval')"
                           :error="errors.update_interval_custom ?? ''">
                        <control :icon="mdiAvTimer" :options="updateIntervalCustomOptions" v-model="form.update_interval_custom"
                                 :required="true"/>
                    </field>
                    <field v-else :label="__('Update interval')"
                           :help="__('0-8 times a day, starting at 6 o\'clock in the morning')"
                           :error="errors.update_interval ?? ''">
                        <control :icon="mdiAvTimer" :options="updateIntervalOptions" v-model="form.update_interval"
                                 :required="true"/>
                    </field>
                    <field v-if="form.feed_type.id === 'google_products'" class="mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field v-if="form.feed_type.id === 'google_products'" class="mb-0" :label="__('Exclude products')">
                        <check-radio-picker
                            class="checkbox-block"
                            name="excluded_products"
                            type="checkbox mr-0"
                            v-model="form.excluded_products"
                            :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                        />
                    </field>
                </div>
                <div>
                    <catalog-selector v-model="form.nomenclatures" :site="formSite"/>
                    <stores-selector v-model="form.stores" :site="formSite"/>
                    <field :label="__('Feed language')" :error="errors.feed_language ?? ''">
                        <control :icon="mdiTranslate" :options="languageOptionData" v-model="form.feed_language"
                                 :required="true" :disabled="languageOptionsDisabled"/>
                    </field>
                </div>
                <div v-if="form.feed_type.id === 'facebook_catalog'">
                    <field :label="__('Store title')" :error="errors.title ?? ''">
                        <control :icon="mdiFormatColorText" v-model="form.title" :disabled="siteMetaDisabled"/>
                    </field>
                    <field :label="__('Store description')" :help="__('Max 9999 characters')">
                        <control :icon="mdiText" type="textarea" v-model="form.description"
                                 :disabled="siteMetaDisabled"/>
                    </field>
                </div>
                <div v-if="form.feed_type.id === 'facebook_catalog'">
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="usd_currency"
                            type="switch"
                            v-model="form.usd_currency"
                            :options="{ usd_currency: __('USD Currency') }"
                        ></check-radio-picker>
                    </field>
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="sample-switch"
                            type="switch"
                            v-model="form.exchange_changes"
                            :options="{ exchange_changes: __('Update when the exchange rate changes') }"
                        />
                    </field>
                </div>
                <div v-if="form.feed_type.id === 'facebook_catalog'">
                    <field class="mb-0" :label="__('Exclude products')">
                        <check-radio-picker
                            class="checkbox-block"
                            name="excluded_products"
                            type="checkbox mr-0"
                            v-model="form.excluded_products"
                            :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                        />
                    </field>
                </div>
            </div>
        </card-component>

        <card-component  v-if="form.feed_type.id === 'xml'" :title="__('Feed Fields Settings')" :icon="mdiViewColumnOutline">
            <div class="md:grid md:grid-cols-2 md:gap-2 mt-4">
                    <field :error="errors.price_fields ?? ''" class="col-span-1">
                        <check-radio-picker class="checkbox-block" type="checkbox mr-0"
                                            name="sample-checkbox"
                                            v-model="form.feed_fields"
                                            :options="feedFieldsOptions"
                        />
                    </field>
                    <card-component class="col-span-1">
                        <field class="max-w-sm mb-2" :label="__('Price additional image')"
                               :error="errors.additional_image ?? ''">
                            <control :icon="mdiImage" :options="additionalImageOptions" v-model="form.additional_image"
                                     :required="true"/>
                        </field>
                        <field class="mt-11">
                            <check-radio-picker
                                class="-ml-7"
                                name="only_available_products"
                                type="switch"
                                v-model="form.only_available_products"
                                :options="{ only_available_products: __('Only availability in stock') }"
                                :readonly="availableProductsReadonly"
                            />
                        </field>
                        <field class="mb-0" :label="__('Exclude products')">
                            <check-radio-picker
                                class="checkbox-block"
                                name="excluded_products"
                                type="checkbox mr-0"
                                v-model="form.excluded_products"
                                :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                            />
                        </field>
                    </card-component>
                </div>
        </card-component>

        <card-component v-if="form.feed_type.id === 'facebook_catalog'"
                        :title="__('Price Price Settings')"
                        :icon="mdiCurrencyUsd"
                        class="col-span-2">
                <field class="col-span-2" :error="errors.price_price ?? ''">
                    <prices-picker class="checkbox-grid" type="radio"
                                   name="price-radio"
                                   v-model="form.price_price"
                                   :options="pricePricesOptions"
                                   :priority="form.price_priority"
                    />
                </field>
            </card-component>

        <jb-buttons class="justify-end" mb="">
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing"
                       type="button" color="success" :label="__('Create :resource', {resource: __('Feed').toLowerCase()})" @click.prevent="submit"/>
            <jb-button type="button" color="warning" outline :label="__('Cancel')"
                       @click.prevent="$inertia.visit(this.route('feed-configs.index'))"/>
        </jb-buttons>

    </BreezeDashboardLayout>
</template>

<style>
</style>

<script>
import BreezeDashboardLayout from '@/Layouts/Dashboard'
import CardComponent from '@/Components/Dashboard/CardComponent'
import TheModalBox from '@/Components/Dashboard/TheModalBox'
import Notification from '@/Components/Dashboard/Notification'
import {Head, usePage} from '@inertiajs/inertia-vue3';
import {computed, ref} from 'vue'
import {
    mdiListStatus,
    mdiAccount,
    mdiEmail,
    mdiLink,
    mdiOpenInNew,
    mdiPuzzleOutline,
    mdiAvTimer,
    mdiTranslate,
    mdiNumeric8BoxMultipleOutline,
    mdiCurrencyUsd,
    mdiViewColumnOutline,
    mdiFormatColorText,
    mdiText,
    mdiImage
} from '@mdi/js'
import Field from '@/Components/Dashboard/Field'
import Control from '@/Components/Dashboard/Control'
import CheckRadioPicker from '@/Components/Dashboard/CheckRadioPicker'
import PricesPicker from '@/Components/Dashboard/PricesPicker'
import JbButton from '@/Components/Dashboard/JbButton'
import JbButtons from '@/Components/Dashboard/JbButtons'
import CatalogSelector from "@/Components/Dashboard/CatalogSelector"
import StoresSelector from "@/Components/Dashboard/StoresSelector"

export default {
    components: {
        BreezeDashboardLayout,
        Head,
        TheModalBox,
        Notification,
        CardComponent,
        Field,
        Control,
        CheckRadioPicker,
        PricesPicker,
        JbButton,
        JbButtons,
        CatalogSelector,
        StoresSelector
    },
    props: {
        errors: Object,
        feedFieldsOptions: Object,
        pricePricesOptions: Object,
        defaultPriceOption: Number,
        feedTypesOptions: Array,
        updateIntervalOptions: Array,
        updateIntervalCustomOptions: Array,
        languageOptions: Array,
        siteOptions: Array,
        additionalImageOptions: Array,
    },
    watch: {
        formSite(newVal, oldVal) {
            if (this.formSiteOldValue === null) {
                if (this.form.nomenclatures.length > 0 || this.form.stores.length > 0) {
                    this.isConfirmChangeSiteModalActive = true
                    this.formSiteOldValue = oldVal
                } else {
                    this.form.site = this.formSite
                    this.setLanguageOptions()
                    this.setSiteMeta()
                }
            } else {
                this.formSiteOldValue = null
            }
        },
        "form.stores": {
            handler(newVAl, oldVal) {
                this.availableProductsToggle(newVAl.length > 0)
            },
            deep: true
        },
        "form.feed_type": {
            handler(newVAl, oldVal) {
                if (newVAl.id === "facebook_catalog") {
                    this.setSiteMeta()
                }
            },
            deep: true
        },
        "form.feed_language": {
            handler(newVAl, oldVal) {
                this.setSiteMeta()
            },
            deep: true
        }
    },
    data(props) {
        return {
            form: this.$inertia.form({
                site: props.siteOptions[0],
                name: '',
                title: '',
                description: '',
                nomenclatures: [],
                stores: [],
                feed_type: props.feedTypesOptions[0],
                update_interval: props.updateIntervalOptions[0],
                update_interval_custom: props.updateIntervalCustomOptions[0],
                usd_currency: false,
                exchange_changes: [],
                price_price: props.defaultPriceOption,
                feed_language: this.languageOptions[0],
                only_available_products: false,
                excluded_products: [],
                feed_fields: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
                additional_image: this.additionalImageOptions[0]
            }),
            formSite: props.siteOptions[0],
            formSiteOldValue: null,
            isLoading: false,
            languageOptionsDisabled: false,
            languageOptionData: props.languageOptions,
            availableProductsReadonly: false,
            siteMetaDisabled: false
        }
    },
    setup() {
        const isConfirmChangeSiteModalActive = ref(false)

        return {
            mdiListStatus,
            mdiAccount,
            mdiLink,
            mdiOpenInNew,
            mdiEmail,
            mdiPuzzleOutline,
            mdiAvTimer,
            mdiTranslate,
            mdiNumeric8BoxMultipleOutline,
            mdiCurrencyUsd,
            mdiViewColumnOutline,
            mdiFormatColorText,
            mdiText,
            mdiImage,
            isConfirmChangeSiteModalActive,
        }
    },
    methods: {
        availableProductsToggle(setAndBlock = true) {
            if(setAndBlock) {
                this.form.only_available_products = true
                this.availableProductsReadonly = true
            } else {
                this.form.only_available_products = false
                this.availableProductsReadonly = false
            }
        },
        getLanguageCodeById(id) {
            let code = 'en'
            if (id == 5) {
                code = 'ru'
            } else if (id == 6) {
                code = 'uk'
            } else if (id == 3) {
                code = 'es'
            }
            return code
        },
        submit() {
            this.form.post(this.route('feed-configs.store'))
        },
        cancelChangeSite() {
            this.formSite = this.formSiteOldValue;
        },
        confirmChangeSite() {
            this.formSiteOldValue = null
            this.form.site = this.formSite
            this.setLanguageOptions()
            this.setSiteMeta()
            this.form.nomenclatures = []
            this.form.stores = []
        },
        setSiteMeta() {
            const self = this
            self.siteMetaDisabled = true
            this.apiRequest('api.v1.site-meta.get', {'siteId': this.form.site.api_source_id, 'languageCode': this.form.feed_language.id}).then(response => {
                self.siteMetaDisabled = false
                if (typeof response.data === "undefined") {
                    return
                }

                if(typeof response.data.title ) {
                    self.form.title = response.data.title
                }

                if(typeof response.data.description ) {
                    self.form.description = response.data.description
                }
            }).catch(error => {
                self.siteMetaDisabled = false
            })
        },
        setLanguageOptions() {
            const self = this
            self.languageOptionsDisabled = true
            this.apiRequest('api.v1.languages.index', {'siteId': this.form.site.api_source_id}).then(response => {
                self.languageOptionsDisabled = false
                if (typeof response.data === "undefined") {
                    return
                }

                self.languageOptionData = response.data.map(language => {
                    return {'id': language.code, 'label': language.name}
                })

                self.$emit('update:languageOptions', self.languageOptionData)

                self.form.feed_language = self.languageOptionData[0]

            }).catch(error => {
                self.languageOptionsDisabled = false
            })
        },
        apiRequest(route, data) {
            const self = this
            return new Promise((resolve, reject) => {
                axios.post(this.route(route), data).then(response => {
                    resolve(response)
                }).catch(error => {
                    console.error(error)
                    reject(error)
                });
            });
        }
    }
}
</script>
